import { StrictMode, useCallback, useMemo } from 'react'
import type { VFC } from 'react'
import {
  canonicalIANATimezoneIdentifier,
  type IANATimezoneIdentifier,
  type IETFLanguageTag,
} from '@opallabs/concepts'
import { Select } from '@opallabs/kuiper'
import type { SelectOption } from '@opallabs/kuiper'
import { buildTimeZoneOptions } from '@opallabs/satellite'

type TimeZoneOption = Readonly<
  Omit<SelectOption, 'id'> & {
    id: IANATimezoneIdentifier
  }
>

type TimeZoneSelectProps = Readonly<{
  date: Date
  disabled?: boolean
  locale?: IETFLanguageTag
  onChange?: (zone?: IANATimezoneIdentifier) => void
  selectedZone?: IANATimezoneIdentifier
}>

/**
 * Wraps `@opallabs/kuiper` `<Select>` with an API that matches
 * `@opallabs/satellite` `<TimeZoneSelect>`, but retains `<Select>`'s Supernova
 * styling.
 *
 * @deprecated Replace with `@opallabs/satellite` `<TimeZoneSelect>`.
 */
export const TimeZoneSelect: VFC<TimeZoneSelectProps> = ({
  date,
  disabled,
  locale = navigator.language,
  onChange,
  selectedZone,
}) => {
  const options: TimeZoneOption[] = useMemo(() => {
    const modernOptions = buildTimeZoneOptions({ date, locale })
    return modernOptions.map(({ label, value }) => ({
      id: value,
      removable: false,
      title: label,
    }))
  }, [date, locale])
  const initialSelection = useMemo(() => {
    if (!selectedZone) return undefined

    const resolvedZone = canonicalIANATimezoneIdentifier(selectedZone)
    return options.find(({ id }) => id === resolvedZone)
  }, [selectedZone, options])
  const wrappedOnChange = useCallback(
    (option?: TimeZoneOption) => {
      if (!onChange || !option) return

      onChange(option.id)
    },
    [onChange]
  )

  return (
    <StrictMode>
      <Select<TimeZoneOption>
        className="time-zone-select"
        onChange={wrappedOnChange}
        placeholder="Select time zone…"
        role="listbox"
        shouldValidate={false}
        theme="dark"
        value={initialSelection}
        {...{ disabled, options }}
      />
    </StrictMode>
  )
}
TimeZoneSelect.displayName = 'Photon: TimeZoneSelect ← DateAndTime'
